/* eslint-disable @typescript-eslint/no-explicit-any */
import { isPlatformBrowser } from '@angular/common';
import { ErrorHandler, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { logException } from '@core/monitoring/otel-logger';
import { UpdateNotificationService } from './update-notification.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(
    private updateNotificationService: UpdateNotificationService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  handleError(error: any): void {
    // Só processa erros no browser, não no SSR
    if (!isPlatformBrowser(this.platformId)) {
      console.error('SSR Error:', error);
      return;
    }

    // Verifica se é erro de import dinâmico (lazy loading)
    const isChunkLoadError = this.isChunkLoadError(error);

    if (isChunkLoadError) {
      console.warn(
        'Chunk morto (deploy trocou os hashes). Forçando refresh para pegar a versão nova.'
      );
      // Force-refresh único, reusando a MESMA trava anti-loop do script de boot do index.html
      // (window.__chunkRecover: no máximo 1 reload por 12s). Se a trava bloquear (já recarregou e
      // ainda falha), NÃO recarrega de novo — cai no aviso "nova versão" pro usuário recarregar à mão.
      const recovered = (window as { __chunkRecover?: () => boolean }).__chunkRecover?.() === true;
      if (!recovered) {
        this.updateNotificationService.notifyUpdateAvailable();
      }
      // Não lançar o erro novamente evita que ele apareça no console como erro não tratado
      return;
    }

    // Envia a exceção estruturada para o SigNoz (`exception.*`) ANTES do console.error, para que
    // o registro carregue tipo/stack de verdade. O hook de console.error do otel-init pegaria a
    // linha de qualquer jeito, mas só como texto — sem stack aproveitável nem agrupamento.
    // No-op quando a observabilidade está desligada (staging/local): logger no-op da API OTel.
    logException(error, { 'vault.error_source': 'angular.errorHandler' });

    console.error('Unhandled Error:', error);
  }

  private isChunkLoadError(error: any): boolean {
    if (!error) return false;

    const errorMessage = error.message || error.toString();
    const stack = error.stack || '';

    // Padrões comuns de erro de chunk loading
    const chunkErrorPatterns = [
      /Failed to fetch dynamically imported module/,
      /Loading chunk.*failed/,
      /ChunkLoadError/,
      /dynamically imported module.*failed/,
    ];

    return chunkErrorPatterns.some(pattern => pattern.test(errorMessage) || pattern.test(stack));
  }
}
